// src/utils/readability.js
// -----------------------------------------------------------------------------
// King's Academy Faculty Tool Kit — reading level estimator
//
// Produces a Lexile-EQUIVALENT estimate. This is NOT an official Lexile® measure.
// "Lexile" is a registered trademark of MetaMetrics, Inc. Their measure is
// computed from a proprietary 3.8-billion-word corpus. This module approximates
// the same two underlying constructs — mean sentence length and mean word
// frequency — using the open SUBTLEX-US frequency corpus, then maps the result
// onto the Lexile scale via calibrated constants.
//
// ALWAYS label output in the UI as "Estimated Reading Level (Lexile-equivalent)".
//
// CALIBRATION
// -----------
// A and B are the published theoretical Lexile coefficients (Lennon & Burdick),
// with B rescaled from natural-log to log10 (Zipf) frequency units (x2.303).
// SCALE and OFFSET were fit by least squares against a six-text calibration
// corpus spanning ~350L to ~1450L; RMSE was 57L (one Lexile standard error is
// 60L). To improve accuracy, run calibrateFromSamples() below against texts with
// published Lexile measures (CommonLit and Newsela both publish theirs) and
// replace SCALE and OFFSET with the values it returns.
// -----------------------------------------------------------------------------

export const CALIBRATION = {
  A: 9.82247,   // weight on log mean sentence length
  B: 4.949,     // weight on mean Zipf word frequency
  SCALE: 39.0,  // fitted — recalibrate against known-Lexile texts if possible
  OFFSET: 831,  // fitted
};

// Zipf value assigned to words absent from the corpus. Low = treated as hard.
const UNKNOWN_ZIPF = 1.4;

// Words below this Zipf are surfaced as "rare" for glossary + revision feedback.
const RARE_ZIPF_THRESHOLD = 3.2;

const FREQ_URL = '/data/wordFrequencies.json';

let _freq = null;
let _freqPromise = null;

/**
 * Lazy-loads the frequency table (~915KB raw, ~263KB gzipped over the wire).
 * Safe to call repeatedly; the fetch happens once.
 */
export async function loadFrequencies() {
  if (_freq) return _freq;
  if (_freqPromise) return _freqPromise;
  _freqPromise = fetch(FREQ_URL)
    .then((r) => {
      if (!r.ok) throw new Error(`Could not load the word frequency list (${r.status}).`);
      return r.json();
    })
    .then((json) => {
      _freq = json;
      return _freq;
    })
    .catch((err) => {
      _freqPromise = null;
      throw err;
    });
  return _freqPromise;
}

export function frequenciesReady() {
  return _freq !== null;
}

// -----------------------------------------------------------------------------
// Grade level <-> Lexile mapping
// MetaMetrics College & Career Ready "stretch" bands.
// -----------------------------------------------------------------------------

export const GRADE_BANDS = [
  { grade: '1',    label: 'Grade 1',        min: 190,  max: 530 },
  { grade: '2',    label: 'Grade 2',        min: 420,  max: 650 },
  { grade: '3',    label: 'Grade 3',        min: 520,  max: 820 },
  { grade: '4',    label: 'Grade 4',        min: 740,  max: 940 },
  { grade: '5',    label: 'Grade 5',        min: 830,  max: 1010 },
  { grade: '6',    label: 'Grade 6',        min: 925,  max: 1070 },
  { grade: '7',    label: 'Grade 7',        min: 970,  max: 1120 },
  { grade: '8',    label: 'Grade 8',        min: 1010, max: 1185 },
  { grade: '9',    label: 'Grade 9',        min: 1050, max: 1260 },
  { grade: '10',   label: 'Grade 10',       min: 1080, max: 1335 },
  { grade: '11-12', label: 'Grades 11–12',  min: 1185, max: 1385 },
];

export function bandForGrade(grade) {
  return GRADE_BANDS.find((b) => b.grade === String(grade)) || null;
}

/** Midpoint of a grade band, rounded to the nearest 10L. */
export function gradeToLexile(grade) {
  const b = bandForGrade(grade);
  if (!b) return null;
  return Math.round((b.min + b.max) / 2 / 10) * 10;
}

/** Human-readable grade description for a given Lexile value. */
export function lexileToGradeLabel(lexile) {
  if (lexile === null || lexile === undefined) return '';
  const hits = GRADE_BANDS.filter((b) => lexile >= b.min && lexile <= b.max);
  if (hits.length === 0) {
    if (lexile < GRADE_BANDS[0].min) return 'Below Grade 1';
    return 'Above Grade 12';
  }
  if (hits.length === 1) return hits[0].label;
  const first = hits[0].grade;
  const last = hits[hits.length - 1].grade;
  return `Grades ${first}–${last}`;
}

// -----------------------------------------------------------------------------
// Tokenising
// -----------------------------------------------------------------------------

const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'st', 'jr', 'sr', 'vs', 'etc', 'fig', 'no',
  'vol', 'approx', 'inc', 'ltd', 'co', 'dept', 'est', 'al', 'ed', 'pp', 'cf',
]);

export function splitSentences(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  const out = [];
  let buf = '';
  const tokens = clean.split(/([.!?]+["'’)\]]*\s)/);
  for (let i = 0; i < tokens.length; i++) {
    buf += tokens[i];
    if (i % 2 === 1 && /[.!?]/.test(tokens[i])) {
      const words = buf.trim().split(/\s+/);
      const last = (words[words.length - 1] || '')
        .replace(/[^A-Za-z.]/g, '')
        .toLowerCase()
        .replace(/\.$/, '');
      // Don't split on "Dr." or a single initial like "J."
      if (ABBREVIATIONS.has(last) || /^[a-z]$/.test(last)) continue;
      out.push(buf.trim());
      buf = '';
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out.filter((s) => /[A-Za-z]/.test(s));
}

export function wordsOf(str) {
  return String(str || '').match(/[A-Za-z][A-Za-z'’-]*/g) || [];
}

export function countWords(str) {
  return wordsOf(str).length;
}

function countSyllables(word) {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!w) return 0;
  if (w.length <= 3) return 1;
  const trimmed = w.replace(/(?:[^laeiouy]es|[^laeiouy]e)$/, '').replace(/^y/, '');
  const groups = trimmed.match(/[aeiouy]{1,2}/g);
  return Math.max(1, groups ? groups.length : 1);
}

function normaliseToken(raw) {
  return raw.toLowerCase().replace(/[’']s$/, '').replace(/^[-’']+|[-’']+$/g, '');
}

// -----------------------------------------------------------------------------
// Main analysis
// -----------------------------------------------------------------------------

/**
 * Analyse a passage. loadFrequencies() must have resolved first.
 *
 * @param {string} text
 * @param {object} [options]
 * @param {string[]} [options.protectedTerms] Teacher-locked terms, excluded from
 *        the frequency calculation so a required technical word doesn't push the
 *        estimate up and trigger a pointless revision loop.
 * @returns {object|null} null if the passage is too short to measure (<25 words)
 */
export function analyzeText(text, options = {}) {
  if (!_freq) throw new Error('Call loadFrequencies() before analyzeText().');

  const protectedSet = new Set(
    (options.protectedTerms || [])
      .flatMap((t) => wordsOf(t))
      .map((t) => t.toLowerCase())
  );

  const sentences = splitSentences(text);
  const totalTextWords = countWords(text);
  if (sentences.length === 0 || totalTextWords < 25) return null;

  let totalWords = 0;
  let totalSyllables = 0;
  let freqSum = 0;
  let freqCount = 0;
  let unknownCount = 0;
  const rare = [];
  const sentenceRecords = [];

  sentences.forEach((sentence, index) => {
    const ws = wordsOf(sentence);
    sentenceRecords.push({ index, length: ws.length, text: sentence });
    totalWords += ws.length;

    ws.forEach((raw, position) => {
      totalSyllables += countSyllables(raw);
      const token = normaliseToken(raw);
      if (!token) return;

      // Skip probable proper nouns: capitalised mid-sentence and unrecognised.
      // A student's reading load for "Amman" isn't the same as for "ubiquitous".
      const looksProper = position > 0 && /^[A-Z]/.test(raw) && _freq[token] === undefined;
      if (looksProper) return;

      if (protectedSet.has(token)) return;

      const zipf = _freq[token];
      if (zipf === undefined) {
        unknownCount += 1;
        freqSum += UNKNOWN_ZIPF;
        freqCount += 1;
        rare.push({ word: raw, zipf: UNKNOWN_ZIPF });
      } else {
        freqSum += zipf;
        freqCount += 1;
        if (zipf < RARE_ZIPF_THRESHOLD) rare.push({ word: raw, zipf });
      }
    });
  });

  const meanSentenceLength = totalWords / sentences.length;
  const meanZipf = freqCount > 0 ? freqSum / freqCount : 4.5;

  const { A, B, SCALE, OFFSET } = CALIBRATION;
  const raw = A * Math.log(meanSentenceLength) - B * meanZipf;
  let lexile = Math.round((SCALE * raw + OFFSET) / 5) * 5;
  lexile = Math.max(-200, Math.min(1800, lexile));

  const fkgl = 0.39 * meanSentenceLength + 11.8 * (totalSyllables / totalWords) - 15.59;

  const seen = new Set();
  const rarestWords = rare
    .filter((r) => {
      const key = r.word.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.zipf - b.zipf);

  return {
    lexile,
    gradeLabel: lexileToGradeLabel(lexile),
    fkgl: Math.round(fkgl * 10) / 10,
    stats: {
      words: totalWords,
      sentences: sentences.length,
      meanSentenceLength: Math.round(meanSentenceLength * 10) / 10,
      meanZipf: Math.round(meanZipf * 100) / 100,
      unknownRate: Math.round((unknownCount / Math.max(1, freqCount)) * 1000) / 10,
      raw: Math.round(raw * 1000) / 1000,
    },
    longestSentences: [...sentenceRecords].sort((a, b) => b.length - a.length).slice(0, 6),
    rarestWords: rarestWords.slice(0, 25),
  };
}

/**
 * Invert the model: what mean sentence length would hit `targetLexile`, given a
 * mean word frequency? Used to give Claude a concrete structural target instead
 * of an abstract number it has no reliable internal sense of.
 */
export function targetSentenceLength(targetLexile, meanZipf) {
  const { A, B, SCALE, OFFSET } = CALIBRATION;
  const raw = (targetLexile - OFFSET) / SCALE;
  const msl = Math.exp((raw + B * meanZipf) / A);
  return Math.max(3, Math.round(msl * 10) / 10);
}

/**
 * A sentence-length window for the prompt. The lower bound assumes Claude also
 * lifts word frequency by ~0.35 Zipf while simplifying (typical), the upper
 * bound assumes vocabulary stays as-is.
 */
export function sentenceLengthWindow(targetLexile, currentMeanZipf) {
  const upper = targetSentenceLength(targetLexile, currentMeanZipf);
  const lower = targetSentenceLength(targetLexile, currentMeanZipf + 0.35);
  return {
    min: Math.min(lower, upper),
    max: Math.max(lower, upper),
  };
}

/**
 * Re-fit SCALE and OFFSET from texts whose real Lexile measure you know.
 * Provide at least 8 samples spread across the range for a usable fit.
 *
 *   calibrateFromSamples([{ text: '...', lexile: 740 }, ...])
 *     -> { SCALE, OFFSET, rmse, n }
 *
 * Paste the returned SCALE and OFFSET into CALIBRATION above.
 */
export function calibrateFromSamples(samples) {
  const points = samples
    .map((s) => {
      const a = analyzeText(s.text);
      return a ? { x: a.stats.raw, y: s.lexile } : null;
    })
    .filter(Boolean);

  const n = points.length;
  if (n < 3) throw new Error('Need at least 3 measurable samples to calibrate.');

  const mx = points.reduce((sum, p) => sum + p.x, 0) / n;
  const my = points.reduce((sum, p) => sum + p.y, 0) / n;
  let num = 0;
  let den = 0;
  points.forEach((p) => {
    num += (p.x - mx) * (p.y - my);
    den += (p.x - mx) ** 2;
  });
  const SCALE = num / den;
  const OFFSET = my - SCALE * mx;
  const rmse = Math.sqrt(
    points.reduce((sum, p) => sum + (SCALE * p.x + OFFSET - p.y) ** 2, 0) / n
  );

  return { SCALE: Math.round(SCALE * 100) / 100, OFFSET: Math.round(OFFSET), rmse: Math.round(rmse), n };
}
